// EventViewer.jsx
import React, { useMemo, useState } from "react";
import {
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    ListItemIcon,
    Chip,
    Box,
    Divider,
    ListItemSecondaryAction,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";

// Icons (MUI v4)
import SportsBasketballIcon from "@material-ui/icons/SportsBasketball";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import ReplayIcon from "@material-ui/icons/Replay";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import TimelapseIcon from "@material-ui/icons/Timelapse";
import PanToolIcon from "@material-ui/icons/PanTool";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";

const useStyles = makeStyles(() => ({
    root: { padding: 16, marginTop: 16, background: "#fafafa" },
    headerRow: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
    },
    filtersRow: {
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "center",
    },
    filterControl: { minWidth: 180 },
    listItem: {
        borderRadius: 12,
        marginBottom: 8,
        background: "#fff",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
    },
    iconWrap: { minWidth: 42 },
    primaryRow: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
    },
    chip: { height: 22, fontWeight: 700 },
    neutralChip: { height: 22 },
    secondaryAction: { right: 12 },
    timeChip: { height: 22 },
}));

const safeString = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));

const normalizeSeconds = (v) => {
    if (v === null || v === undefined) return null;
    return v > 100000 ? Math.round(v / 1000) : Math.round(v);
};

const formatClock = (secondsMaybeMs) => {
    const s = normalizeSeconds(secondsMaybeMs);
    if (s === null) return "-";
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
};

const getTipoUI = (tipo) => {
    switch (tipo) {
        case "punto":
            return { icon: <SportsBasketballIcon />, label: "Punto" };
        case "quitar_punto":
            return { icon: <RemoveCircleOutlineIcon />, label: "Quitar punto" };
        case "falta":
            return { icon: <ReportProblemIcon />, label: "Falta" };
        case "quitar_falta":
            return { icon: <ReplayIcon />, label: "Quitar falta" };
        case "cambio":
            return { icon: <SwapHorizIcon />, label: "Cambio" };
        case "cambiosale":
            return { icon: <SwapHorizIcon />, label: "Sale" };
        case "inicio_crono":
            return { icon: <PlayArrowIcon />, label: "Iniciar crono" };
        case "pausa_crono":
            return { icon: <PauseIcon />, label: "Pausar crono" };
        case "cambio_periodo":
            return { icon: <TimelapseIcon />, label: "Cambio periodo" };
        case "violation":
            return { icon: <PanToolIcon />, label: "Violación" };
        case "quitar_violation":
            return { icon: <ReplayIcon />, label: "Quitar violación" };
        case "assist":
            return { icon: <TrendingUpIcon />, label: "Asistencia" };
        case "quitar_assist":
            return { icon: <ReplayIcon />, label: "Quitar asistencia" };
        case "rebound":
            return { icon: <TrendingUpIcon />, label: "Rebote" };
        case "quitar_rebound":
            return { icon: <ReplayIcon />, label: "Quitar rebote" };
        default:
            return { icon: <TimelapseIcon />, label: safeString(tipo || "Evento") };
    }
};

const EventViewer = ({ gameId, gameData }) => {
    const classes = useStyles();

    const gameEvents = useSelector((state) => state.teamgames.gameEvents);
    const loading = useSelector((state) => state.teamgames.loadingGameEvents);

    // ✅ NUEVA ESTRUCTURA: gameData.estado.teamAInfo / teamBInfo con teamColor
    const estado = gameData?.estado || {};
    const teamAInfo = estado?.teamAInfo || {};
    const teamBInfo = estado?.teamBInfo || {};

    const teamMeta = useMemo(() => {
        const A = {
            name: teamAInfo?.name || "Equipo A",
            color: teamAInfo?.teamColor || "#ef4122",
            players: Array.isArray(teamAInfo?.players) ? teamAInfo.players : [],
        };
        const B = {
            name: teamBInfo?.name || "Equipo B",
            color: teamBInfo?.teamColor || "#1f2937",
            players: Array.isArray(teamBInfo?.players) ? teamBInfo.players : [],
        };

        const buildMap = (players) => {
            const m = {};
            players.forEach((p) => {
                const key = safeString(p?.number).trim();
                if (key) m[key] = p?.name || "";
            });
            return m;
        };

        return { A, B, mapA: buildMap(A.players), mapB: buildMap(B.players) };
    }, [teamAInfo, teamBInfo]);

    const resolveTeamName = (equipo) => {
        if (equipo === "A") return teamMeta.A.name;
        if (equipo === "B") return teamMeta.B.name;
        return null;
    };

    const resolveTeamColor = (equipo) => {
        if (equipo === "A") return teamMeta.A.color;
        if (equipo === "B") return teamMeta.B.color;
        return null;
    };

    const resolvePlayerName = (equipo, numero) => {
        if (!numero || numero === "team") return null;
        const key = safeString(numero).trim();
        if (equipo === "A") return teamMeta.mapA[key] || null;
        if (equipo === "B") return teamMeta.mapB[key] || null;
        return null;
    };

    const playerLabel = (equipo, numero) => {
        if (numero === null || numero === undefined) return "-";
        if (numero === "team") return "Equipo";
        const name = resolvePlayerName(equipo, numero);
        return name ? `#${numero} • ${name}` : `#${numero}`;
    };

    // ---- Orden + Tipos disponibles ----
    const sorted = useMemo(() => {
        const arr = Array.isArray(gameEvents) ? [...gameEvents] : [];
        arr.sort((a, b) => (b?.timestamp || 0) - (a?.timestamp || 0));
        return arr;
    }, [gameEvents]);

    const tiposDisponibles = useMemo(() => {
        const set = new Set();
        sorted.forEach((ev) => ev?.tipo && set.add(ev.tipo));
        return Array.from(set).sort();
    }, [sorted]);

    // ---- Filtros ----
    const [tipoFilter, setTipoFilter] = useState("ALL");
    const [equipoFilter, setEquipoFilter] = useState("ALL"); // ALL | A | B

    const filtered = useMemo(() => {
        return sorted.filter((ev) => {
            const okTipo = tipoFilter === "ALL" ? true : ev?.tipo === tipoFilter;
            const okEquipo =
                equipoFilter === "ALL"
                    ? true
                    : (ev?.equipo || "") === equipoFilter;
            return okTipo && okEquipo;
        });
    }, [sorted, tipoFilter, equipoFilter]);

    // ---- Detalle por evento (sin duplicar) ----
    const buildDetail = (ev) => {
        const cuarto = ev?.cuarto ?? "-";
        const equipoNombre = resolveTeamName(ev?.equipo);
        const jugador = playerLabel(ev?.equipo, ev?.numero);

        switch (ev?.tipo) {
            case "punto":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • ${jugador} +${ev?.cantidad ?? "-"}`;
            case "quitar_punto":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • ${jugador} -${ev?.cantidad ?? "-"}`;
            case "falta":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Falta de ${jugador}`;
            case "quitar_falta":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Quitar falta ${jugador}`;
            case "cambio": {
                const entra = playerLabel(ev?.equipo, ev?.numero);
                const saleOk = ev?.numberOut && ev.numberOut !== "NA";
                const sale = saleOk ? playerLabel(ev?.equipo, ev.numberOut) : null;
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Entra ${entra}${sale ? `, sale ${sale}` : ""}`;
            }
            case "cambiosale":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Sale ${jugador}`;
            case "inicio_crono":
            case "pausa_crono":
                return `${cuarto != null ? `Cuarto ${cuarto} • ` : ""}Reloj: ${formatClock(ev?.tiempoSegundos)} (${normalizeSeconds(ev?.tiempoSegundos) ?? "-"}s)`;
            case "cambio_periodo":
                return `Nuevo cuarto: ${cuarto} • Tiempo: ${formatClock(ev?.tiempoSegundos)} (${normalizeSeconds(ev?.tiempoSegundos) ?? "-"}s)`;
            case "violation":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Violación ${jugador}`;
            case "quitar_violation":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Quitar violación ${jugador}`;
            case "assist":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Asistencia ${jugador}`;
            case "quitar_assist":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Quitar asistencia ${jugador}`;
            case "rebound":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Rebote ${jugador}`;
            case "quitar_rebound":
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Quitar rebote ${jugador}`;
            default:
                return `${equipoNombre ? equipoNombre + " • " : ""}Cuarto ${cuarto} • Jugador ${jugador}`;
        }
    };

    const renderTeamChip = (equipo) => {
        const name = resolveTeamName(equipo);
        if (!name) return null;
        const bg = resolveTeamColor(equipo) || "#9ca3af";
        return (
            <Chip
                size="small"
                className={classes.chip}
                label={name}
                style={{ backgroundColor: bg, color: "#fff" }}
            />
        );
    };

    return (
        <Paper className={classes.root}>
            <Box className={classes.headerRow}>
                <Box>
                    <Typography variant="h6">Eventos Registrados</Typography>
                    <Box mt={0.5} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Chip
                            size="small"
                            className={classes.neutralChip}
                            label={`${filtered.length} / ${sorted.length}`}
                            variant="outlined"
                        />
                    </Box>
                </Box>

                {/* ✅ Filtros */}
                <Box className={classes.filtersRow}>
                    <FormControl variant="outlined" size="small" className={classes.filterControl}>
                        <InputLabel>Tipo</InputLabel>
                        <Select value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)} label="Tipo">
                            <MenuItem value="ALL">Todos</MenuItem>
                            {tiposDisponibles.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {getTipoUI(t).label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" className={classes.filterControl}>
                        <InputLabel>Equipo</InputLabel>
                        <Select value={equipoFilter} onChange={(e) => setEquipoFilter(e.target.value)} label="Equipo">
                            <MenuItem value="ALL">Todos</MenuItem>
                            <MenuItem value="A">{teamMeta.A.name}</MenuItem>
                            <MenuItem value="B">{teamMeta.B.name}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Box mt={1} mb={1}>
                <Divider />
            </Box>

            {loading ? (
                <CircularProgress />
            ) : (
                <List dense>
                    {filtered.map((ev, index) => {
                        const { icon, label } = getTipoUI(ev?.tipo);
                        const hora = ev?.timestamp ? new Date(ev.timestamp).toLocaleTimeString() : "-";

                        return (
                            <ListItem key={ev?.id || index} className={classes.listItem}>
                                <ListItemIcon className={classes.iconWrap}>{icon}</ListItemIcon>

                                <ListItemText
                                    primary={
                                        <Box className={classes.primaryRow}>
                                            <Chip size="small" className={classes.chip} label={label} variant="outlined" />
                                            {renderTeamChip(ev?.equipo)}
                                        </Box>
                                    }
                                    secondary={buildDetail(ev)}
                                />

                                <ListItemSecondaryAction className={classes.secondaryAction}>
                                    <Chip size="small" className={classes.timeChip} label={hora} variant="outlined" />
                                </ListItemSecondaryAction>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Paper>
    );
};

export default EventViewer;
